import * as XLSX from "xlsx";

import { applyCpfMaskIfNeeded, CPF_LENGTH } from "@/utils/cpf-mask";
import { downloadFromBlob } from "@/utils/download-blob";
import { isValidCPF } from "@/utils/format-cps";

const TEMPLATE_SAMPLE_SIZE = 10;
const TEMPLATE_SHEET_NAME = "CPFs";
const TEMPLATE_FILE_NAME = "modelo-cpfs-segmentacao.xlsx";
const CPF_COLUMN_WIDTH = 18;
const NOTE_COLUMN_WIDTH = 52;

const CPF_COLUMN_HEADER = "cpf";
const NOTE_COLUMN_HEADER = "observacao";
const SAMPLE_NOTE = "exemplo - apague esta linha e cole os CPFs reais";

const XLSX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const FIRST_CHECK_DIGIT_WEIGHT = 10;
const SECOND_CHECK_DIGIT_WEIGHT = 11;

function checkDigit(digits: string, startWeight: number): number {
  const sum = digits
    .split("")
    .reduce((acc, digit, index) => acc + Number(digit) * (startWeight - index), 0);
  const remainder = (sum * 10) % 11;
  return remainder === 10 ? 0 : remainder;
}

export function buildSyntheticCpf(sequence: number): string {
  const base = String(sequence).padStart(CPF_LENGTH.WITHOUT_MASK - 2, "0");
  const first = checkDigit(base, FIRST_CHECK_DIGIT_WEIGHT);
  const second = checkDigit(`${base}${first}`, SECOND_CHECK_DIGIT_WEIGHT);
  return `${base}${first}${second}`;
}

export function syntheticCpfSamples(count = TEMPLATE_SAMPLE_SIZE): string[] {
  const samples: string[] = [];
  let sequence = 1;
  while (samples.length < count) {
    const cpf = buildSyntheticCpf(sequence);

    if (isValidCPF(cpf)) samples.push(cpf);
    sequence++;
  }
  return samples;
}

export function buildCpfTemplateBlob(): Blob {
  const rows = [
    [CPF_COLUMN_HEADER, NOTE_COLUMN_HEADER],
    ...syntheticCpfSamples().map((cpf) => [
      applyCpfMaskIfNeeded(cpf),
      SAMPLE_NOTE,
    ]),
  ];

  const sheet = XLSX.utils.aoa_to_sheet(rows);
  sheet["!cols"] = [{ wch: CPF_COLUMN_WIDTH }, { wch: NOTE_COLUMN_WIDTH }];
  for (let row = 1; row <= rows.length - 1; row++) {
    const cell = sheet[XLSX.utils.encode_cell({ c: 0, r: row })];
    if (cell) cell.t = "s";
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, TEMPLATE_SHEET_NAME);

  const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  return new Blob([buffer], { type: XLSX_MIME_TYPE });
}

export function downloadCpfTemplate(fileName = TEMPLATE_FILE_NAME): void {
  downloadFromBlob(buildCpfTemplateBlob(), fileName);
}

export function isSpreadsheetFile(file: File): boolean {
  return /\.xlsx?$/i.test(file.name) || file.type === XLSX_MIME_TYPE;
}

export async function spreadsheetToCsvFile(file: File): Promise<File> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(new Uint8Array(buffer), { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows: unknown[][] = sheet
    ? XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: true, raw: false })
    : [];

  const content = rows
    .map((row) => String(row?.[0] ?? "").trim())
    .join("\r\n");
  return new File([content], `${file.name}.csv`, { type: "text/csv" });
}

export function toImportableCpfFile(file: File): Promise<File> {
  if (!isSpreadsheetFile(file)) return Promise.resolve(file);
  return spreadsheetToCsvFile(file);
}
